import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../mock';
import { useAuth } from '../context/AuthContext';

// Privacy-friendly visitor tracker.
// - Generates an anonymous visitor_id (UUID) stored in localStorage so we can
//   count "unique visitors" without ever touching PII.
// - Generates a session_id stored in sessionStorage that resets when the tab
//   closes — used to count "sessions" / bounce rate.
// - Fires one POST /api/analytics/track per route change, fire-and-forget.
const STORAGE_KEY = 'tai_visitor_id';
const SESSION_KEY = 'tai_session_id';
const UTM_KEY = 'tai_utm';

function makeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getVisitorId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = makeId();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch { return 'v_anon'; }
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = makeId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch { return 's_anon'; }
}

// UTM attribution: captured once from the landing URL, remembered for the
// whole session so every pageview in it is credited to the ad/post link.
function getUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get('utm_source');
    if (src) {
      const utm = {
        utm_source: src.slice(0, 120),
        utm_medium: (params.get('utm_medium') || '').slice(0, 120),
        utm_campaign: (params.get('utm_campaign') || '').slice(0, 120),
      };
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
      return utm;
    }
    const saved = sessionStorage.getItem(UTM_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
}

// Paths we should never track.
const SKIP_PREFIXES = ['/admin', '/auth', '/order/success', '/login'];

export default function useAnalytics() {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef('');

  useEffect(() => {
    if (user?.role === 'admin') return;          // never track our own staff
    const path = location.pathname || '/';
    if (SKIP_PREFIXES.some(p => path.startsWith(p))) return;
    if (lastPath.current === path) return;        // avoid double-fire on re-renders
    lastPath.current = path;

    // Fire-and-forget. We never want analytics to break the UX.
    // Uses apiClient (sends the session cookie) so the backend can also
    // detect and exclude signed-in admins server-side.
    const body = {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      path,
      referrer: document.referrer || '',
      ...getUtm(),
    };
    apiClient.post('/analytics/track', body, { timeout: 5000 }).catch(() => {});
  }, [location.pathname, user]);
}
